import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import type { FeatureCollection, Feature, Polygon, MultiPolygon, Point } from 'geojson';
import type { GmuResolutionResult, ResolvedGmu } from '@/types/gmu';

// ---------------------------------------------------------------------------
// State GMU configuration
// ---------------------------------------------------------------------------

interface StateGmuConfig {
  /** GeoJSON filename under public/data/gmu/ */
  file: string;
  /** Label prefix for Tier 1 states (e.g. "DMU", "WMU"). Empty for county-based states. */
  labelPrefix: string;
}

/**
 * Configuration for every supported state.
 *
 * Tier 1 — dedicated management units (state DNR ArcGIS REST APIs):
 *   WI (DMU), PA (WMU), MN (DPA), MI (DMU)
 *
 * Tier 2 — county-based (US Census TIGER/Line):
 *   NY, IL, IA, OH, TX, GA, NC, KY, IN, VA, AL, SC, MO
 *
 * Tier 3 (future expansion): additional states added on demand.
 */
export const STATE_GMU_CONFIG: Record<string, StateGmuConfig> = {
  WI: { file: 'wi.geojson', labelPrefix: 'DMU' },
  PA: { file: 'pa.geojson', labelPrefix: 'WMU' },
  MN: { file: 'mn.geojson', labelPrefix: 'DPA' },
  MI: { file: 'mi.geojson', labelPrefix: 'DMU' },
  NY: { file: 'ny.geojson', labelPrefix: '' },
  IL: { file: 'il.geojson', labelPrefix: '' },
  IA: { file: 'ia.geojson', labelPrefix: '' },
  OH: { file: 'oh.geojson', labelPrefix: '' },
  TX: { file: 'tx.geojson', labelPrefix: '' },
  GA: { file: 'ga.geojson', labelPrefix: '' },
  NC: { file: 'nc.geojson', labelPrefix: '' },
  KY: { file: 'ky.geojson', labelPrefix: '' },
  IN: { file: 'in.geojson', labelPrefix: '' },
  VA: { file: 'va.geojson', labelPrefix: '' },
  AL: { file: 'al.geojson', labelPrefix: '' },
  SC: { file: 'sc.geojson', labelPrefix: '' },
  MO: { file: 'mo.geojson', labelPrefix: '' },
};

/**
 * Set of all states with GMU data available.
 * Exported for use in GmuDisplay and other consumers.
 */
export const COVERED_STATES = new Set(Object.keys(STATE_GMU_CONFIG));

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface GmuFeatureProperties {
  unit_id: string;
  unit_label: string;
  // Legacy fields (kept for backward compat)
  unitId: string;
  unitName: string;
  display: string;
  stateCode: string;
}

type GmuFeature = Feature<Polygon | MultiPolygon, GmuFeatureProperties>;
type GmuFeatureCollection = FeatureCollection<Polygon | MultiPolygon, GmuFeatureProperties>;

// In-memory cache: avoids repeated fetches within a session
const gmuCache = new Map<string, GmuFeatureCollection>();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidCoordinate(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;
  return true;
}

async function loadGmuData(state: string): Promise<GmuFeatureCollection> {
  const cached = gmuCache.get(state);
  if (cached) return cached;

  const config = STATE_GMU_CONFIG[state];
  // SAFETY: caller already confirmed state is in COVERED_STATES before calling loadGmuData
  const filename = config.file;

  const response = await fetch(`/data/gmu/${filename}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching GMU data for ${state}`);
  }
  // CAST: we trust the static GeoJSON files we ship match the schema
  const data = (await response.json()) as GmuFeatureCollection;
  gmuCache.set(state, data);
  return data;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function resolveGmu(input: {
  state: string;
  latitude: number | null | undefined;
  longitude: number | null | undefined;
}): Promise<GmuResolutionResult> {
  const { state, latitude, longitude } = input;

  if (!isValidCoordinate(latitude, longitude)) {
    return { status: 'no_coordinates' };
  }

  if (!COVERED_STATES.has(state)) {
    return { status: 'unsupported_state' };
  }

  // At this point latitude/longitude are valid finite numbers — safe to use
  // CAST: isValidCoordinate guarantees these are non-null finite numbers
  const lat = latitude as number;
  // CAST: isValidCoordinate guarantees these are non-null finite numbers
  const lng = longitude as number;

  let featureCollection: GmuFeatureCollection;
  try {
    featureCollection = await loadGmuData(state);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { status: 'error', message };
  }

  // GeoJSON point: [longitude, latitude] order
  const point: Feature<Point> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Point', coordinates: [lng, lat] },
  };

  for (const feature of featureCollection.features) {
    const gmuFeature = feature as GmuFeature;
    if (booleanPointInPolygon(point, gmuFeature)) {
      const props = gmuFeature.properties;
      // Prefer the normalized `unit_id` / `unit_label` fields from the new GeoJSON schema.
      // Fall back to legacy `unitId` / `display` for any grandfathered files.
      const unitId = props.unit_id ?? props.unitId;
      const display = props.unit_label ?? props.display;
      const unitName = props.unit_label ?? props.unitName;

      const resolved: ResolvedGmu = {
        stateCode: state,
        unitId,
        unitName,
        display,
      };
      return { status: 'resolved', gmu: resolved };
    }
  }

  return { status: 'outside_coverage' };
}

/** Clear the in-memory GeoJSON cache — used in tests to reset state between runs. */
export function clearGmuCache(): void {
  gmuCache.clear();
}
